import { useEffect, useMemo, useState } from 'react';

type BrowserPermission = NotificationPermission | 'unsupported';

export function useNotificationPermission() {
  const isSupported = typeof window !== 'undefined' && 'Notification' in window;
  const [permission, setPermission] = useState<BrowserPermission>(() =>
    isSupported ? Notification.permission : 'unsupported',
  );

  useEffect(() => {
    if (!isSupported || !navigator.permissions?.query) {
      return;
    }

    let permissionStatus: PermissionStatus | undefined;

    navigator.permissions
      .query({ name: 'notifications' as PermissionName })
      .then((status) => {
        permissionStatus = status;
        setPermission(Notification.permission);
        status.onchange = () => setPermission(Notification.permission);
      })
      .catch(() => setPermission(Notification.permission));

    return () => {
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, [isSupported]);

  const requestPermission = useMemo(
    () => async () => {
      if (!isSupported) {
        setPermission('unsupported');
        return 'unsupported';
      }

      const nextPermission = await Notification.requestPermission();
      setPermission(nextPermission);
      return nextPermission;
    },
    [isSupported],
  );

  return {
    permission,
    isSupported,
    requestPermission,
  };
}
